<template>
  <div class="container-table">
    <div v-if="showTabulator"
      class="options-download row mb-2 col-md-12 justify-content-between align-items-center p-0">
      <div class="d-flex flex-grow-2">
        <b-button id="download-csv" pill size="sm" variant="secondary" class="m-1 align-items-center"
          :disabled="downloading" @click="downloadCSV">
          <csv-icon style="font-size: 1.1em; vertical-align: -3%" />
          Baixar CSV
        </b-button>
        <b-button id="download-xlsx" @click="downloadXLSX" pill size="sm" variant="secondary" class="m-1"
          :disabled="downloading">
          <excel-icon style="font-size: 1.1em; vertical-align: -3%" />
          Baixar XLSX
        </b-button>
        <b-button id="download-pdf" @click="downloadPDF" pill size="sm" variant="secondary" class="m-1"
          :disabled="downloading">
          <pdf-icon style="font-size: 1.1em; vertical-align: -3%" />
          Baixar PDF
        </b-button>
        <span v-if="downloading" class="ml-1 align-self-center">Preparando arquivo...</span>
      </div>
      <div class="total-events m-0 pl-3">Eventos registrados : {{ totalEventsCount }}</div>
    </div>
    <div v-if="onCreate" class="row mb-2 col-md-12 justify-content-end p-0">
      <b-button
        id="add-event"
        @click="openAddEventModal"
        size="sm"
        variant="success"
        class="m-1"
      >
        Adicionar evento
      </b-button>
    </div>
    <div ref="table" class="table" v-show="showTabulator"></div>
    <b-modal
      v-if="onCreate"
      v-model="showAddEventModal"
      title="Adicionar evento"
      ok-title="Salvar"
      cancel-title="Cancelar"
      :ok-disabled="!newEventNote.trim() || savingEvent"
      @ok="handleAddEvent"
    >
      <p class="mb-2">A data/hora registrada será a do momento da inserção.</p>
      <b-form-textarea
        v-model="newEventNote"
        placeholder="Descreva o evento..."
        rows="3"
        maxlength="200"
      />
    </b-modal>
  </div>
</template>

<script>
import Tabulator from "tabulator-tables";
import moment from "moment";
export default {
  name: "tableEvents",
  props: {
    data: {
      type: Array,
      required: false,
      default: [],
    },
    isMobile: {
      type: Boolean,
      default: false,
    },
    chartMounted: Boolean,
    // quando informada, a tabela busca cada página do servidor (paginação
    // remota) em vez de usar o array completo recebido em `data`.
    fetchPage: {
      type: Function,
      default: null,
    },
    // usado para nomear os arquivos exportados (ex.: "VALVE_01_eventos.csv")
    equipmentName: {
      type: String,
      default: "",
    },
    // quando informada, exibe o botão "Adicionar evento"; recebe a mensagem
    // digitada e deve retornar uma Promise (chamada da API de inserção)
    onCreate: {
      type: Function,
      default: null,
    },
  },
  data() {
    return {
      tabulator: null,
      tableData: [],
      isSortingNoteNull: true,
      sortingText: "Com anotações",
      showTabulator: false,
      downloading: false,
      remoteTotal: 0,
      remoteTotalPages: 0,
      limit: 60,
      remotePageSize: 50,
      showAddEventModal: false,
      newEventNote: "",
      savingEvent: false,
      columns: [
        {
          title: "Data",
          field: "NAME",
          sorter: "string",
          headerFilterLiveFilter: false,
          // headerFilter: "input",
          hozAlign: "center",
          resizable: false,
          formatter: "datetime",
          widthGrow: 1,
          variableHeight: true,
          formatterParams: {
            inputFormat: "YYYY-MM-DD HH:mm:ss",
            outputFormat: "DD/MM/YY - HH:mm:ss",
            invalidPlaceholder: "(Data inválida)",
          },
          headerFilter: this.minMaxFilterEditor
        },
        {
          title: "Anotação",
          field: "VALUE",
          sorter: "string",
          variableHeight: true,
          headerFilterPlaceholder: "Filtrar por nomes dos eventos...",
          hozAlign: "left",
          headerFilter: "input",
          widthGrow: 2,
          resizable: false,
          formatData: [],
          // NOTE chega delimitada pelo firmware ($mensagem$, evento automático)
          // ou por nós (&usuário: mensagem&, evento manual). O delimitador é só
          // marcação — nunca deve aparecer na tela — então aqui a gente detecta
          // qual é, marca a linha (pro formatter destacar eventos manuais) e
          // retira o delimitador antes de sanitizar o texto.
          mutator: function (value, data) {
            if (!value) return value;
            const trimmed = value.trim();
            const isManual = /^&[\s\S]*&$/.test(trimmed);
            const isAutomatic = /^\$[\s\S]*\$$/.test(trimmed);
            const unwrapped =
              isManual || isAutomatic ? trimmed.slice(1, -1).trim() : trimmed;

            if (data) data.IS_MANUAL_NOTE = isManual;

            // O formatter injeta o valor como HTML (célula não-manual devolve a
            // string crua pro Tabulator), então "<"/">" viram entidade em vez de
            // caractere literal -- senão o whitelist abaixo os removeria (evita
            // que uma nota com "<" seja interpretada como início de tag) e
            // mensagens com comparação (ex.: fw_cir "Pressurizado (0.8 bar >= 0.3
            // bar)") perdiam justamente o operador.
            const escaped = unwrapped.replace(/</g, "&lt;").replace(/>/g, "&gt;");

            return escaped.replace(
              /[^a-z-A-Z0-9áàâãéèêíïóôõöúçñÁÀÂÃÉÈÍÏÓÔÕÖÚÇÑ()³²':./=&;\s ]/g,
              " "
            );
          },
          formatter: function (cell) {
            const value = cell.getValue() || "";
            if (!cell.getData().IS_MANUAL_NOTE) return value;

            const span = document.createElement("span");
            span.style.fontWeight = "bold";
            span.style.color = "#28a745";
            span.textContent = value;
            return span;
          },
          // CSV/XLSX/PDF não renderizam HTML -- desfaz o &lt;/&gt; do mutator
          // pra não vazar a entidade literal no arquivo exportado.
          accessorDownload: function (value) {
            return typeof value === "string"
              ? value.replace(/&lt;/g, "<").replace(/&gt;/g, ">")
              : value;
          },
        },
      ],
    };
  },
  computed: {
    totalEventsCount() {
      return this.fetchPage ? this.remoteTotal : this.data.length;
    },
    baseFilename() {
      return this.equipmentName
        ? `${this.equipmentName}_eventos`
        : "Relatório-eventos";
    },
  },
  watch: {
    chartMounted() {
      if (this.fetchPage) return;
      setTimeout(() => {
        this.pages = Math.ceil(this.data.length / this.limit);
        this.tabulator.setData(this.data);
        this.showTabulator = true;
      }, 500);
    },
  },
  mounted() {
    window.moment = moment;

    const sharedOptions = {
      layout: "fitColumns",
      columns: this.columns,
      responsiveLayout: true,
      minHeight: "10%",
      autoResize: true,
      maxHeight: "100%",
      locale: true,
      langs: {
        "pt-br": {
          pagination: {
            "first": "Primeira", //text for the first page button
            "first_title": "First Page", //tooltip text for the first page button
            "last": "Última página",
            "last_title": "Última página",
            "prev": "Anterior",
            "prev_title": "Página anterior",
            "next": "Próxima",
            "next_title": "Próxima página",
          }
        }
      },
      initialSort: [
        { column: "NAME", dir: "desc" }, //sort by this first
      ],
      downloadConfig: {
        columnHeaders: true, //do not include column headers in downloaded table
        columnGroups: true, //do not include column groups in column headers for downloaded table
        rowGroups: true, //do not include row groups in downloaded table
        columnCalcs: false, //do not include column calcs in downloaded table
        dataTree: false, //do not include data tree in downloaded table
      },
    };

    if (this.fetchPage) {
      this.tabulator = new Tabulator(this.$refs.table, {
        ...sharedOptions,
        pagination: "remote",
        paginationSize: this.remotePageSize,
        ajaxURL: "internal://actuator-events",
        ajaxRequestFunc: (url, config, params) =>
          this.fetchPage(params.page).then((result) => {
            this.remoteTotal = result.total;
            this.remoteTotalPages = result.totalPages;
            this.showTabulator = true;
            return { data: result.rows, last_page: result.totalPages || 1 };
          }),
      });
    } else {
      this.tabulator = new Tabulator(this.$refs.table, {
        ...sharedOptions,
        pagination: "local",
        data: [], //link data to table
        paginationSize: this.limit,
      });
    }
  },
  methods: {
    formatterAnnotation(cell, formatterParams, onRendered) {
      return cell
        .getValue()
        .replace(/[^a-z-A-Z0-9áàâãéèêíïóôõöúçñÁÀÂÃÉÈÍÏÓÔÕÖÚÇÑ()³²'\s ]/g, " ");
    },
    minMaxFilterEditor(cell, onRendered, success, cancel, editorParams) {
      //cell - the cell component for the editable cell
      //onRendered - function to call when the editor has been rendered
      //success - function to call to pass the successfuly updated value to Tabulator
      //cancel - function to call to abort the edit and return to a normal cell
      //editorParams - params object passed into the editorParams column definition property
      var container = document.createElement("span");
      //create and style inputs
      var start = document.createElement("input");
      start.setAttribute("type", "date");
      start.setAttribute("placeholder", "Min");
      start.style.padding = "2px";
      start.style.width = "100%";
      start.style.border = "solid 1px #fafafa";
      start.style.backgroundColor = "#fafafa";
      start.style.boxSizing = "border-box";
      start.id = "dateTimeSelector";
      start.value = cell.getValue();
      const onChange = (e) => {
        // this.tabulator.element.querySelectorAll(".tabulator-col.tabulator-sortable")[0].style.height="74px"
        // this.tabulator.element.querySelectorAll(".tabulator-col.tabulator-sortable")[1].style.height="74px"
        // Apagar do registro de filtros do tabulagor (this.tabulator.getFilter())
        // todos os filtros criados com o field NAME (Filtros de data)
        this.clearFiltersTabulator("NAME");
        if (e.target.value) {
          cell
            .getColumn()
            .getTable()
            .addFilter(
              "NAME",
              "starts",
              moment(e.target.value).format("YYYY-MM-DD")
            );
        }
      };
      cell.getColumn().getTable().addFilter("NAME", "starts", "");
      start.addEventListener("change", onChange);
      start.addEventListener("blur", onChange);
      container.appendChild(start);
      return container;
    },
    clearFiltersTabulator(fieldName) {
      var allFilters = this.tabulator.getFilters();
      for (var filter = 0; filter < allFilters.length; filter += 1) {
        if (allFilters[filter]["field"] === fieldName) {
          this.tabulator.removeFilter(
            fieldName,
            allFilters[filter].type,
            allFilters[filter].value
          );
        }
      }
    },
    changeSortingAnotation(type) {
      this.tabulator
        .getColumn("NAME")
        ._column.element.querySelector("#dateTimeSelector").value = "";
      this.clearFiltersTabulator("NAME");
      if (type == "all") {
        this.clearFiltersTabulator("VALUE");
        this.sortingText = "Todos";
        return;
      }
      this.tabulator.setFilter("VALUE", "!=", "-----", { matchAll: true });
      this.sortingText = "Com anotações";
    },
    openAddEventModal() {
      this.newEventNote = "";
      this.showAddEventModal = true;
    },
    handleAddEvent(bvModalEvt) {
      bvModalEvt.preventDefault();
      const note = this.newEventNote.trim();
      if (!note || this.savingEvent) return;

      this.savingEvent = true;
      this.onCreate(note)
        .then(() => {
          this.showAddEventModal = false;
          this.reload();
        })
        .catch((err) => {
          console.error(err);
          alert("Não foi possível adicionar o evento. Tente novamente.");
        })
        .finally(() => {
          this.savingEvent = false;
        });
    },
    reload() {
      // Paginação remota (atuador): volta pra página 1, que já reconsulta o
      // servidor. Paginação local (sensor/DIR): o pai (onCreate) já
      // reprocessou this.data antes dessa Promise resolver, mas a prop só
      // reflete o novo array depois que o componente pai re-renderiza — o
      // que o Vue 2 adia pro nextTick, não é síncrono com a reatribuição.
      // Sem o nextTick aqui, this.data ainda seria o array antigo.
      if (this.fetchPage) this.tabulator.setPage(1);
      else this.$nextTick(() => this.tabulator.setData(this.data));
    },
    downloadXLSX() {
      this.runDownload("xlsx", `${this.baseFilename}.xlsx`, {
        sheetName: `Relatório-Eventos`,
      });
    },
    downloadCSV() {
      this.runDownload("csv", `${this.baseFilename}.csv`, {
        bom: true,
      });
    },
    downloadPDF() {
      this.runDownload("pdf", `${this.baseFilename}.pdf`, {
        documentProcessing: function (workbook) {
          workbook.Props = {
            Title: "Relatório",
            Subject: "3v3",
            CreatedDate: new Date(2017, 12, 19),
          };
          return workbook;
        },
      });
    },
    // Na paginação remota o Tabulator só mantém a página atual (50 linhas)
    // em memória, então o download precisa buscar todas as páginas do
    // servidor antes de gerar o arquivo, para manter o comportamento
    // anterior de exportar o histórico completo.
    async runDownload(type, filename, options) {
      if (!this.fetchPage) {
        this.tabulator.download(type, filename, options);
        return;
      }
      if (this.downloading) return;

      this.downloading = true;
      try {
        const totalPages = this.remoteTotalPages || 1;
        let rows = [];
        for (let page = 1; page <= totalPages; page++) {
          const result = await this.fetchPage(page);
          rows = rows.concat(result.rows);
        }

        const el = document.createElement("div");
        el.style.position = "absolute";
        el.style.left = "-9999px";
        document.body.appendChild(el);
        let tempTabulator;
        await new Promise((resolve) => {
          tempTabulator = new Tabulator(el, {
            data: rows,
            columns: this.columns,
            tableBuilt: () => resolve(),
          });
        });
        tempTabulator.download(type, filename, options);
        tempTabulator.destroy();
        el.remove();
      } catch (err) {
        console.error(err);
        alert("Não foi possível gerar o arquivo. Tente novamente.");
      } finally {
        this.downloading = false;
      }
    },
  },
};
</script>

<style scoped>
.total-events {
  font-size: smaller;
}

.pagination-table {
  padding: 4px;
  margin-left: 4px;
  background-color: #f0f0f0;
  border-radius: 4px;
  cursor: context-menu;
  border: solid 1px #dedede;
}

.table2 {
  visibility: hidden;
  height: 0;
}

.container-table {
  display: flex;
  flex-wrap: wrap;
  padding-bottom: 5px;
  width: 100%;
  /* height: 100%; */
  padding-top: 0.5em;
  align-content: flex-end;
}

.container-table>>>.tabulator .tabulator-header .tabulator-col .tabulator-col-content .tabulator-col-title {
  white-space: normal;
}

.container-table>>>.tabulator {
  font-family: "Arial", sans-serif;
  font-size: 13px;
  border: none;
  border-radius: 4px;
  /* padding: 0 4px; */
}

.container-table>>>.tabulator {
  font-family: "Arial", sans-serif;
  font-size: 13px;
  border: none;
  border-radius: 4px;
  /* padding: 0 4px; */
}

.container-table>>>.tabulator .tabulator-header {
  font-weight: normal;
  border-bottom: 1px solid;
  border-bottom-color: rgba(0, 0, 0, 0.12);
  color: rgba(0, 0, 0, 0.54);
}

.container-table>>>.tabulator .tabulator-header .tabulator-col {
  height: 75px;
  background-color: #e6e6e6;
  padding: 0 6px;
  font-size: 15px;
  text-align: center;
  font-weight: 600;
  border: none !important;
}

.container-table>>>.tabulator .tabulator-tableHolder {
  overflow: hidden;
}

.container-table>>>.tabulator-row {
  border-bottom: 1px solid;
  border-bottom-color: rgba(0, 0, 0, 0.12);
  min-height: 48px;
  vertical-align: middle;
}

.container-table>>>.tabulator-row .tabulator-cell {
  padding: 16px 7px;
  vertical-align: middle;
}

.container-table>>>.tabulator-row .tabulator-cell:first-of-type,
.container-table>>>.tabulator .tabulator-header .tabulator-col:first-of-type {
  padding-left: 24px;
}

.container-table>>>.tabulator-row.tabulator-row-even {
  background-color: #f7f7f7;
}
</style>
